// Shortlist + journal, stored in Postgres per signed-in user.
import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';

export interface JournalEntry {
  id: string;
  date: string;
  time: string;
  symbol: string;
  sector: string;
  side: 'bull' | 'bear';
  priceAtAdd: number;
  pctAtAdd: number;
  score: number;
  withSector: boolean;
  note: string;
  review: string;
  createdAt: string;
}

export type NewEntry = Omit<JournalEntry, 'id' | 'note' | 'review' | 'createdAt'> & { note?: string };

/** Move since shortlisting, and whether it's in the shortlisted direction. */
export function since(e: Pick<JournalEntry, 'priceAtAdd' | 'side'>, now: number) {
  const chg = ((now - e.priceAtAdd) / e.priceAtAdd) * 100;
  const flat = Math.abs(chg) < 0.005;
  const good = !flat && (e.side === 'bull' ? chg > 0 : chg < 0);
  return { chg, good, cls: flat ? 'flat' : good ? 'good' : 'bad' };
}

export function useJournal() {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const r = await api('/v1/journal');
    if (r.ok) setEntries(await r.json());
    else setError('Could not load your journal');
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const add = useCallback(async (e: NewEntry) => {
    const r = await api('/v1/journal', { method: 'POST', body: JSON.stringify(e) });
    if (!r.ok) return setError('Could not add to shortlist');
    const saved: JournalEntry = await r.json();
    setEntries((list) => [saved, ...list]);
  }, []);

  const update = useCallback(async (id: string, patch: { note?: string; review?: string }) => {
    setEntries((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    const r = await api(`/v1/journal/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
    if (!r.ok) setError('Could not save your note');
  }, []);

  const remove = useCallback(async (id: string) => {
    setEntries((list) => list.filter((x) => x.id !== id));
    const r = await api(`/v1/journal/${id}`, { method: 'DELETE' });
    if (!r.ok) { setError('Could not remove the entry'); void reload(); }
  }, [reload]);

  const find = useCallback((date: string, symbol: string) => entries.find((e) => e.date === date && e.symbol === symbol), [entries]);

  return { entries, error, add, update, remove, find, reload };
}

export function exportCsv(entries: JournalEntry[]) {
  const cols = ['date', 'time', 'symbol', 'sector', 'side', 'priceAtAdd', 'pctAtAdd', 'score', 'withSector', 'note', 'review'] as const;
  const cell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [cols.join(','), ...entries.map((e) => cols.map((c) => cell(e[c])).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = `moneyhq-journal-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
